import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import type {
  ProfileNameTitle,
  ProfileSalutation,
} from "@/lib/profile/types";
import type { User } from "@supabase/supabase-js";

export type SignUpInput = {
  email: string;
  password: string;
  fullName: string;
  salutation: ProfileSalutation;
  nameTitle: ProfileNameTitle | "";
  occupation: "physician" | "healthcare_assistant";
  professionalTitle: string;
  registryNumber: string;
  phone: string;
  city: string;
  privacyPolicyAccepted: boolean;
  termsAccepted: boolean;
  newsletterSubscribed: boolean;
};

export function isAuthSessionMissing(error: unknown) {
  if (!error || typeof error !== "object") {
    return false;
  }

  const name = "name" in error ? String(error.name) : "";
  const message = "message" in error ? String(error.message).toLowerCase() : "";

  return (
    name === "AuthSessionMissingError" ||
    message.includes("auth session missing")
  );
}

export async function getBrowserAuthUser(): Promise<User | null> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error && !isAuthSessionMissing(error)) {
    throw error;
  }

  return user ?? null;
}

export async function signInWithPassword(email: string, password: string) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  return data;
}

export async function signUpWithProfile(input: SignUpInput) {
  if (!input.privacyPolicyAccepted || !input.termsAccepted) {
    throw new Error(
      "A regisztrációhoz el kell fogadnia az adatvédelmi szabályzatot és a felhasználói feltételeket.",
    );
  }

  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      data: {
        full_name: input.fullName,
        salutation: input.salutation,
        name_title: input.nameTitle,
        occupation: input.occupation,
        professional_title: input.professionalTitle,
        registry_number: input.registryNumber,
        phone: input.phone,
        city: input.city,
        privacy_policy_accepted: input.privacyPolicyAccepted,
        terms_accepted: input.termsAccepted,
        newsletter_subscribed: input.newsletterSubscribed,
      },
    },
  });

  if (error) {
    throw error;
  }

  return data;
}

export async function signOut() {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.auth.signOut();

  if (error) {
    throw error;
  }
}
